import { useEffect, useState } from "react";
import { Lock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

export const CoachNotes = ({ submissionId }) => {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/submissions/${submissionId}/notes`).then((r) => setNotes(r.data)).catch(() => {});
  }, [submissionId]);

  const add = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/submissions/${submissionId}/notes`, { text });
      setNotes([data, ...notes]);
      setText("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    await api.delete(`/notes/${id}`);
    setNotes(notes.filter((n) => n.id !== id));
  };

  return (
    <section className="no-print rounded-2xl border border-amber-200 bg-amber-50/60 p-5" data-testid="coach-notes">
      <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
        <Lock className="h-4 w-4 text-amber-700" /> Private coaching notes
      </h2>
      <p className="text-xs text-slate-600">Only admins can see these. They never appear on the participant's link or in printouts.</p>
      <Textarea data-testid="coach-note-input" rows={3} className="mt-3 bg-white" placeholder="Add a note about this person's results, call or next steps…" value={text} onChange={(e) => setText(e.target.value)} />
      <Button data-testid="coach-note-save" onClick={add} disabled={busy || !text.trim()} className="mt-2 rounded-full bg-[#1F4266] hover:bg-[#122A43]">Add note</Button>
      <div className="mt-4 space-y-3">
        {notes.map((n) => (
          <div key={n.id} className="rounded-xl bg-white p-3 text-sm shadow-sm" data-testid={`coach-note-${n.id}`}>
            <div className="flex items-center justify-between text-[11px] text-slate-500">
              <span>{new Date(n.created_at).toLocaleString()} · {n.author}</span>
              <button onClick={() => remove(n.id)} data-testid={`coach-note-delete-${n.id}`} className="text-slate-400 hover:text-red-600" aria-label="Delete note">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="mt-1 whitespace-pre-wrap text-slate-800">{n.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
};
